export interface TimezoneOption {
  key: string;
  value: string;
}

interface BuildTimezoneOptionsParams {
  locale?: string;
  includeLocalFirst?: boolean;
  maxTotal?: number; // soft cap, default 60
}

interface CuratedZone {
  name: string;
  cities: string;
}

interface ResolvedZone {
  name: string;
  cities: string;
  offsetMinutes: number;
}

export class TimezoneUtils {
  /**
   * One representative IANA zone per inhabited UTC offset, ordered roughly
   * west-to-east. Uninhabited mid-ocean zones are intentionally omitted, and
   * each entry lists several well-known cities that share the zone. The live
   * UTC offset and the localized zone name are derived from Intl at runtime,
   * so DST transitions stay accurate without bundling a timezone database.
   */
  private static readonly CURATED_ZONES: readonly CuratedZone[] = [
    { name: 'Pacific/Pago_Pago', cities: 'Pago Pago, Midway' },
    { name: 'Pacific/Honolulu', cities: 'Honolulu, Papeete' },
    { name: 'America/Anchorage', cities: 'Anchorage, Juneau' },
    { name: 'America/Los_Angeles', cities: 'Los Angeles, Vancouver, Tijuana' },
    { name: 'America/Denver', cities: 'Denver, Calgary, Phoenix' },
    { name: 'America/Chicago', cities: 'Chicago, Mexico City, Winnipeg' },
    { name: 'America/New_York', cities: 'New York, Toronto, Bogotá, Lima' },
    { name: 'America/Halifax', cities: 'Halifax, Caracas, Santo Domingo' },
    { name: 'America/St_Johns', cities: "St. John's" },
    { name: 'America/Sao_Paulo', cities: 'São Paulo, Buenos Aires, Montevideo' },
    { name: 'Atlantic/Cape_Verde', cities: 'Praia, Azores' },
    { name: 'Europe/London', cities: 'London, Dublin, Lisbon' },
    { name: 'Europe/Zurich', cities: 'Zurich, Paris, Berlin, Rome, Madrid' },
    { name: 'Europe/Athens', cities: 'Athens, Helsinki, Cairo, Johannesburg, Kyiv' },
    { name: 'Europe/Istanbul', cities: 'Istanbul, Moscow, Riyadh, Nairobi, Baghdad' },
    { name: 'Asia/Tehran', cities: 'Tehran' },
    { name: 'Asia/Dubai', cities: 'Dubai, Baku, Tbilisi, Yerevan' },
    { name: 'Asia/Kabul', cities: 'Kabul' },
    { name: 'Asia/Karachi', cities: 'Karachi, Tashkent, Yekaterinburg' },
    { name: 'Asia/Kolkata', cities: 'Mumbai, Delhi, Kolkata, Colombo' },
    { name: 'Asia/Kathmandu', cities: 'Kathmandu' },
    { name: 'Asia/Dhaka', cities: 'Dhaka, Almaty' },
    { name: 'Asia/Yangon', cities: 'Yangon' },
    { name: 'Asia/Bangkok', cities: 'Bangkok, Jakarta, Ho Chi Minh City, Hanoi' },
    { name: 'Asia/Shanghai', cities: 'Shanghai, Beijing, Singapore, Hong Kong, Manila, Perth, Taipei' },
    { name: 'Asia/Tokyo', cities: 'Tokyo, Seoul' },
    { name: 'Australia/Adelaide', cities: 'Adelaide, Darwin' },
    { name: 'Australia/Sydney', cities: 'Sydney, Melbourne, Brisbane, Guam' },
    { name: 'Pacific/Guadalcanal', cities: 'Honiara, Nouméa' },
    { name: 'Pacific/Auckland', cities: 'Auckland, Wellington, Suva' },
    { name: 'Pacific/Tongatapu', cities: "Nuku'alofa, Apia" },
    { name: 'Pacific/Kiritimati', cities: 'Kiritimati' },
  ];

  private static formatUtcOffset(totalMinutes: number): string {
    const sign = totalMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(totalMinutes);
    const hh = String(Math.floor(abs / 60)).padStart(2, '0');
    const mm = String(abs % 60).padStart(2, '0');
    return `UTC${sign}${hh}:${mm}`;
  }

  private static cityFromIana(name: string): string {
    return (name.split('/').pop() ?? name).replace(/_/g, ' ');
  }

  private static computeOffsetMinutes(ianaName: string, now: Date): number {
    const localIso = now.toLocaleString('sv', { timeZone: ianaName });
    const offsetMs = new Date(localIso + 'Z').getTime() - now.getTime();
    return Math.round(offsetMs / 60000);
  }

  private static buildLabel(zone: ResolvedZone, locale: string, now: Date): string {
    const offset = TimezoneUtils.formatUtcOffset(zone.offsetMinutes);

    const zoneName =
      Intl.DateTimeFormat(locale, {
        timeZone: zone.name,
        timeZoneName: 'longGeneric',
      })
        .formatToParts(now)
        .find((p) => p.type === 'timeZoneName')?.value ?? TimezoneUtils.cityFromIana(zone.name);

    return `${offset} - ${zoneName} - ${zone.cities}`;
  }

  public static getUtcOffsetString(ianaTimezone: string | null): string {
    if (!ianaTimezone) return '';
    const now = new Date();
    const localIso = now.toLocaleString('sv', { timeZone: ianaTimezone });
    const offsetMs = new Date(localIso + 'Z').getTime() - now.getTime();
    const totalMinutes = Math.round(offsetMs / 60000);
    return TimezoneUtils.formatUtcOffset(totalMinutes);
  }

  public static buildTimezoneOptions(params: BuildTimezoneOptionsParams = {}): TimezoneOption[] {
    const { locale = 'en', includeLocalFirst = true, maxTotal = 60 } = params;

    const localTz = includeLocalFirst ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined;
    const now = new Date();

    // Resolve the curated zones, excluding the local zone (it is prepended below).
    const resolved: ResolvedZone[] = TimezoneUtils.CURATED_ZONES.filter((zone) => zone.name !== localTz).map((zone) => ({
      name: zone.name,
      cities: zone.cities,
      offsetMinutes: TimezoneUtils.computeOffsetMinutes(zone.name, now),
    }));

    // Sort the rest west-to-east by their current UTC offset.
    resolved.sort((a, b) => a.offsetMinutes - b.offsetMinutes);

    // Place the user's local zone first, reusing curated cities when available.
    if (localTz) {
      const curatedLocal = TimezoneUtils.CURATED_ZONES.find((zone) => zone.name === localTz);
      resolved.unshift({
        name: localTz,
        cities: curatedLocal?.cities ?? TimezoneUtils.cityFromIana(localTz),
        offsetMinutes: TimezoneUtils.computeOffsetMinutes(localTz, now),
      });
    }

    return resolved.slice(0, maxTotal).map((zone) => ({
      key: zone.name,
      value: TimezoneUtils.buildLabel(zone, locale, now),
    }));
  }
}
